import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Invoice, CombinedInvoice, CombinedInvoiceStatus, ID } from '@shared/types';
import {
  buildBaseFields,
  buildUpdateFields,
  fromSnapshot,
  generateCombinedCode,
  writeAuditLog,
} from '@shared/utils';

const COL = 'combined_invoices';
const INV_COL = 'invoices';

export async function fetchCandidateInvoices(
  customerId: ID,
  closingYearMonth: string,
  areaId?: string | null,
): Promise<Invoice[]> {
  const q = query(
    collection(db, INV_COL),
    where('isDeleted', '==', false),
    where('customerId', '==', customerId),
    where('combinedInvoiceId', '==', null),
    where('status', 'not-in', ['cancelled']),
  );
  const snap = await getDocs(q);
  const invoices = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Invoice);

  const [yr, mo] = closingYearMonth.split('-').map(Number);
  const from = new Date(yr, mo - 1, 1).getTime();
  const to = new Date(yr, mo, 0, 23, 59, 59, 999).getTime();

  return invoices.filter((inv) => {
    const ts = (inv.issueDate as Timestamp).toMillis();
    const inPeriod = ts >= from && ts <= to;
    if (!inPeriod) return false;
    // areaId 未指定 or null の場合は顧客全体を対象
    if (areaId === undefined || areaId === null) return true;
    return (inv as Invoice & { areaId?: string | null }).areaId === areaId;
  });
}

const combinedInvoiceCreateSchema = z.object({
  customerId: z.string().min(1, '顧客は必須です'),
  areaId: z.string().nullable().default(null),
  closingYearMonth: z.string().regex(/^\d{4}-\d{2}$/, '締め期間は YYYY-MM 形式で入力してください'),
  invoiceIds: z.array(z.string()).min(1, '対象請求書を1件以上選択してください'),
  issueDate: z.instanceof(Timestamp),
  notes: z.string().nullable().default(null),
});

export type CombinedInvoiceCreateInput = z.infer<typeof combinedInvoiceCreateSchema>;

export async function createCombinedInvoice(
  data: CombinedInvoiceCreateInput,
  userId: ID,
): Promise<CombinedInvoice> {
  const validated = parseOrThrow(combinedInvoiceCreateSchema, data);

  const invSnaps = await Promise.all(
    validated.invoiceIds.map((id) => getDoc(doc(db, INV_COL, id)))
  );

  invSnaps.forEach((snap, i) => {
    if (!snap.exists()) {
      throw new Error(`請求書 ${validated.invoiceIds[i]} が見つかりません。`);
    }
    if (snap.data()['combinedInvoiceId']) {
      throw new Error(`請求書 ${validated.invoiceIds[i]} は既に合算済みです。`);
    }
    if (snap.data()['customerId'] !== validated.customerId) {
      throw new Error(`請求書 ${validated.invoiceIds[i]} の顧客が一致しません。`);
    }
  });

  const grandtotal = invSnaps.reduce(
    (sum, snap) => sum + ((snap.data()?.['grandtotal'] ?? snap.data()?.['total'] ?? 0) as number),
    0,
  );

  const code = await generateCombinedCode();
  const payload = {
    ...validated,
    code,
    grandtotal,
    status: 'draft' as CombinedInvoiceStatus,
    ...buildBaseFields(userId),
  };

  const batch = writeBatch(db);
  const ciRef = doc(collection(db, COL));
  batch.set(ciRef, payload);

  validated.invoiceIds.forEach((invId) => {
    batch.update(doc(db, INV_COL, invId), {
      combinedInvoiceId: ciRef.id,
      ...buildUpdateFields(userId),
    });
  });

  await batch.commit();

  await writeAuditLog({
    entityType: 'combined_invoice',
    entityId: ciRef.id,
    action: 'create',
    userId,
    after: validated,
  });

  return { id: ciRef.id, ...payload } as unknown as CombinedInvoice;
}

export async function fetchCombinedInvoices(filters?: {
  customerId?: ID;
  status?: CombinedInvoiceStatus;
}): Promise<CombinedInvoice[]> {
  let q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('issueDate', 'desc'),
  );
  if (filters?.customerId) {
    q = query(q, where('customerId', '==', filters.customerId));
  }
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CombinedInvoice);
}

export async function fetchCombinedInvoice(id: ID): Promise<CombinedInvoice | null> {
  return fromSnapshot<CombinedInvoice>(await getDoc(doc(db, COL, id)));
}

export async function issueCombinedInvoice(id: ID, userId: ID): Promise<CombinedInvoice> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('合算請求書が見つかりません。');
  const before = snap.data();
  const patch = { status: 'issued' as CombinedInvoiceStatus, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'combined_invoice',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: { status: 'issued' },
  });
  return { id, ...before, ...patch } as unknown as CombinedInvoice;
}

export async function deleteCombinedInvoice(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('合算請求書が見つかりません。');
  const data = snap.data() as CombinedInvoice;

  const batch = writeBatch(db);
  batch.update(ref, { isDeleted: true, ...buildUpdateFields(userId) });

  (data.invoiceIds ?? []).forEach((invId) => {
    batch.update(doc(db, INV_COL, invId), {
      combinedInvoiceId: null,
      ...buildUpdateFields(userId),
    });
  });

  await batch.commit();

  await writeAuditLog({
    entityType: 'combined_invoice',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
    after: null,
  });
}
